import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment.dev';
import { USER_ROLE } from '../models/enums';
import { IUser } from '../models/model';
import { AuthenticationService } from '../services/authentication.service';
import { SubscriptionStateService } from '../services/subscription-state.service';
import { DocGuard } from './doc.guard';


@Injectable({ providedIn: 'root' })
export class SubscriptionGuard implements CanActivate {
    currentUser!: IUser | null | undefined
    constructor(
        private router: Router,
        private authService: AuthenticationService,
        private subscriptionState: SubscriptionStateService,
        private docGuard: DocGuard,
    ) {
        this.authService.currentUser.subscribe(res => {
            this.currentUser = res
        });
    }

    async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
        // Skip subscription check if disabled in environment
        if (!environment.enableSubscription) {
            return Promise.resolve(true);
        }

        // ✅ ALWAYS ALLOW: Auth routes (login, signup, password reset, etc.)
        const authRoutes = [
            '/login',
            '/signup',
            '/forgot-password',
            '/reset-password',
            '/onboard',
            '/verify',
            '/invitation-signup',
            '/email-auth'
        ];
        const isAuthRoute = authRoutes.some(authRoute => state.url.startsWith(authRoute));
        if (isAuthRoute) {
            return Promise.resolve(true);
        }

        // ✅ ALWAYS ALLOW: OAuth callbacks
        if (state.url.includes('/oauth/') || state.url.includes('/callback')) {
            return Promise.resolve(true);
        }

        // ✅ ALWAYS ALLOW: Pricing page (users need to activate their plan)
        if (state.url.includes('/pricing')) {
            // Not allowed to access pricing page if user is a member
            if (this.currentUser?.role == USER_ROLE.MEMBER) {
                this.router.navigate(['']);
                return Promise.resolve(false);
            }
            return Promise.resolve(true);
        }

        // ✅ STEP 1: CHECK AUTHENTICATION STATUS FIRST
        // Handle edit-document route for non-authenticated users (external document sharing)
        // This should be checked BEFORE subscription status
        if (!this.currentUser && state.url.includes('edit-document')) {
            const docGuardResult = await this.docGuard.canActivate(route);
            if (!docGuardResult) {
                return Promise.resolve(false);
            }
            // Allow anonymous edit-document access even if subscription ended
            return Promise.resolve(true);
        }

        // Handle anonymous users (external users case) - allow edit-document access
        if (this.currentUser?.anonymous) {
            // Anonymous users can access edit-document
            if (state.url.includes('edit-document')) {
                return Promise.resolve(true);
            }
            // Block other routes for anonymous users
            this.router.navigate(['/login']);
            return Promise.resolve(false);
        }

        // ✅ STEP 2: CHECK SUBSCRIPTION STATUS (only for authenticated users)
        // ✅ ALWAYS ALLOW: Explore page when payment is in progress (Stripe redirect)
        // Check for payment success indicators in URL query params or session storage
        const sessionId = route.queryParams['session_id'] || new URLSearchParams(window.location.search).get('session_id');
        const paymentSuccessFlag = sessionStorage.getItem('payment_success');
        const isPaymentInProgress = !!(sessionId && paymentSuccessFlag);

        // ✅ ALWAYS ALLOW: Explore page when subscription ended (safe landing page for dialog)
        // This allows users to see the "Activate your plan" dialog
        let subscriptionDetails = this.subscriptionState.subscriptionDetails();
        let hasSubscriptionData = !!subscriptionDetails;

        // ⚠️ IMPORTANT: If subscription data hasn't loaded yet (race condition in new window),
        // wait for it to load before making a decision
        if (!hasSubscriptionData) {
            try {
                // Wait for subscription data to load
                await firstValueFrom(this.subscriptionState.fetchSubscriptionDetails());
                subscriptionDetails = this.subscriptionState.subscriptionDetails();
                hasSubscriptionData = !!subscriptionDetails;
            } catch (error) {
                // On error, allow access to prevent blocking legitimate users
                return Promise.resolve(true);
            }
        }

        const hasSubscriptionEnded = this.subscriptionState.hasSubscriptionEnded();

        if ((hasSubscriptionEnded || isPaymentInProgress) && (state.url.includes('/explore') || state.url === '/')) {
            // Reset dialog dismissal to ensure dialog shows (unless payment is in progress)
            if (!isPaymentInProgress) {
                this.subscriptionState.resetDialogDismissal();
            }
            return Promise.resolve(true);
        }

        // ✅ BLOCK ALL OTHER ROUTES if subscription has ended
        if (hasSubscriptionEnded) {
            // Reset dialog dismissal to ensure dialog shows when subscription ends
            this.subscriptionState.resetDialogDismissal();

            // Allow certain routes even when subscription has ended (only explore, pricing, and root)
            // Compare route requires active subscription
            const allowedRoutesWhenEnded = ['/explore', '/pricing'];
            const isAllowedRoute = allowedRoutesWhenEnded.some(allowedRoute =>
                state.url.includes(allowedRoute) || state.url === '/'
            );

            if (isAllowedRoute) {
                return Promise.resolve(true);
            }

            // Block navigation and redirect to explore (safe landing page)
            // The dialog will show automatically via app.component.html
            // Redirect to explore page (dialog will show there)
            // This includes /compare route - it requires active subscription
            this.router.navigate(['/explore']).catch(() => {
                // If navigation fails, try root
                this.router.navigate(['']).catch(() => { });
            });

            return Promise.resolve(false);
        }

        // Subscription is active or in trial - allow access
        return Promise.resolve(true);
    }
}
